import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Monitor, Calendar,
  LayoutDashboard, Building2, LogOut, User, Settings, ImageIcon, LayoutGrid,
  Menu, X, BarChart3,
  type LucideIcon,
} from 'lucide-react';

import AdminFacilitySelector from '@/components/AdminFacilitySelector';
import GuideDownloads from '@/components/GuideDownloads';
import { Badge } from '@/components/ui/badge';
import { useAuth, useAuthActions } from '@/store/auth';
import { APP_VERSION } from '@/version';

// ─── Nav structure ─────────────────────────────────────────────────────────────
interface NavItem {
  label: string;
  href: string;
  icon: LucideIcon;
}
interface NavSection {
  label: string;
  items: NavItem[];
}

const ADMIN_NAV: NavSection[] = [
  {
    label: 'Club Manager',
    items: [
      { label: 'Slideshows', href: '/slideshows', icon: LayoutGrid },
      { label: 'Streaming',  href: '/schedules',  icon: Calendar },
    ],
  },
  {
    label: 'Overlays',
    items: [
      { label: 'Overlays', href: '/overlays', icon: Monitor },
      { label: 'Logos',    href: '/logos',    icon: ImageIcon },
    ],
  },
  {
    label: 'Admin',
    items: [
      { label: 'Dashboard',      href: '/admin',              icon: LayoutDashboard },
      { label: 'Reports',        href: '/reports',            icon: BarChart3 },
      { label: 'Facilities',     href: '/facilities',         icon: Building2 },
      { label: 'Admin Settings', href: '/admin?tab=settings', icon: Settings },
    ],
  },
];

// Facility users: only Slideshows + Schedule
const FACILITY_NAV: NavSection[] = [
  {
    label: 'Slideshows',
    items: [
      { label: 'My Slideshows', href: '/slideshows', icon: LayoutGrid },
    ],
  },
  {
    label: 'Broadcast',
    items: [
      { label: 'Streaming', href: '/schedules', icon: Calendar },
      { label: 'Reports',   href: '/reports',   icon: BarChart3 },
    ],
  },
];

// ─── Active link detection ──────────────────────────────────────────────────────
// Handles both plain paths and paths with query strings (e.g. /admin?tab=assets)
function useIsActive() {
  const location = useLocation();
  return (href: string): boolean => {
    const [path = '', qs] = href.split('?');
    if (qs) {
      // Must match both pathname and query param
      return location.pathname === path && location.search.includes(qs);
    }
    if (path === '/admin') {
      // "Dashboard" is active on /admin only when no conflicting tab query is set
      return location.pathname === '/admin' && !location.search.includes('tab=');
    }
    return location.pathname.startsWith(path);
  };
}

// ─── Layout ────────────────────────────────────────────────────────────────────
interface LayoutProps {
  children: React.ReactNode;
}

export default function Layout({ children }: LayoutProps) {
  const { user } = useAuth();
  const { logout } = useAuthActions();
  const navigate = useNavigate();
  const location = useLocation();
  const isActive = useIsActive();

  const [drawerOpen, setDrawerOpen] = useState(false);

  // Close the drawer whenever the route changes
  useEffect(() => { setDrawerOpen(false); }, [location.pathname, location.search]);

  // Lock body scroll while drawer is open (mobile only)
  useEffect(() => {
    if (!drawerOpen) return;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, [drawerOpen]);

  const handleLogout = () => { logout(); navigate('/'); };

  const sections = user?.role === 'admin' ? ADMIN_NAV : FACILITY_NAV;

  return (
    <div className="flex min-h-screen bg-background">

      {/* ── Mobile top bar (md and smaller) ── */}
      <div className="fixed inset-x-0 top-0 z-30 flex h-14 items-center justify-between border-b border-gray-200 bg-white px-3 dark:border-gray-700 dark:bg-gray-900 md:hidden">
        <button
          onClick={() => setDrawerOpen(true)}
          className="rounded-md p-2 text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800"
          aria-label="Open navigation menu"
        >
          <Menu className="h-5 w-5" />
        </button>
        <div className="flex items-center gap-2">
          <img
            src="/curling-stadium-logo.png"
            alt="CSM"
            className="h-6 w-6 object-contain"
            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
          />
          <span className="text-sm font-bold tracking-tight">CSM</span>
        </div>
        <div className="w-9" /> {/* spacer to balance hamburger */}
      </div>

      {/* ── Drawer backdrop (mobile only, when open) ── */}
      {drawerOpen && (
        <div
          onClick={() => setDrawerOpen(false)}
          className="fixed inset-0 z-40 bg-black/50 md:hidden"
          aria-hidden="true"
        />
      )}

      {/* ── Sidebar ── */}
      <aside className={`fixed inset-y-0 left-0 z-50 flex w-56 flex-col bg-slate-900 text-slate-100 transition-transform duration-200 ease-out
        ${drawerOpen ? 'translate-x-0' : '-translate-x-full'}
        md:translate-x-0`}
      >

        {/* Logo */}
        <div className="flex items-center gap-2.5 border-b border-slate-800 px-4 py-4">
          <Link to="/" className="flex items-center gap-2">
            <img
              src="/curling-stadium-logo.png"
              alt="CSM"
              className="h-7 w-7 object-contain"
              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
            />
            <span className="text-base font-bold tracking-tight">CSM</span>
          </Link>
          <Badge variant="secondary" className="ml-auto text-[10px] px-1.5 py-0.5" title={`Frontend v${APP_VERSION}`}>v{APP_VERSION}</Badge>
          {/* Close button (mobile only) */}
          <button
            onClick={() => setDrawerOpen(false)}
            className="ml-1 rounded p-1 text-slate-400 hover:bg-slate-800 hover:text-slate-200 md:hidden"
            aria-label="Close navigation menu"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Facility context */}
        {user && (
          <div className="border-b border-slate-800 px-3 py-2.5">
            {user.role === 'admin' ? (
              <AdminFacilitySelector />
            ) : (
              <div className="flex items-center gap-2 rounded-md bg-slate-800 px-2.5 py-1.5">
                <Building2 className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                <span className="truncate text-xs font-medium text-slate-200">{user.facilityId}</span>
                <Badge variant="outline" className="ml-auto shrink-0 border-slate-600 text-[10px] text-slate-400">
                  Club Manager
                </Badge>
              </div>
            )}
          </div>
        )}

        {/* Nav sections */}
        <nav className="flex-1 overflow-y-auto px-2 py-3">
          {sections.map((section, si) => (
            <div key={section.label} className={si > 0 ? 'mt-4' : ''}>
              <p className="mb-1 px-2 text-[10px] font-semibold uppercase tracking-widest text-slate-500">
                {section.label}
              </p>
              <ul className="space-y-0.5">
                {section.items.map((item) => {
                  const active = isActive(item.href);
                  const Icon = item.icon;
                  return (
                    <li key={item.href}>
                      <Link
                        to={item.href}
                        className={`flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm transition-colors ${
                          active
                            ? 'bg-blue-600 text-white'
                            : 'text-slate-300 hover:bg-slate-800 hover:text-slate-100'
                        }`}
                      >
                        <Icon className="h-4 w-4 shrink-0" />
                        {item.label}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </nav>

        {/* Downloadable guides (role-scoped) */}
        <GuideDownloads />

        {/* User info + logout */}
        <div className="border-t border-slate-800 px-3 py-3">
          <div className="mb-2 flex items-center gap-2 px-1">
            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-700">
              <User className="h-3.5 w-3.5 text-slate-300" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-xs font-medium text-slate-200">
                {user?.facilityId ?? user?.sub ?? '—'}
              </p>
              <p className="text-[10px] capitalize text-slate-500">
                {user?.role === 'facility' ? 'Club Manager' : user?.role}
              </p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-sm text-slate-400 transition-colors hover:bg-slate-800 hover:text-slate-100"
          >
            <LogOut className="h-4 w-4 shrink-0" />
            Log out
          </button>
        </div>
      </aside>

      {/* ── Main content ── */}
      {/* On mobile: full-width with top padding for the fixed mobile top bar.
          On desktop (md+): left-padded by the fixed sidebar width. */}
      <main className="flex-1 overflow-auto pt-14 md:ml-56 md:pt-0">
        {children}
      </main>
    </div>
  );
}
